import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { Button } from '../components/ui'
import { useFunnelConfig } from '../funnel/useFunnel'
import { eventOnce, initialSessionId } from '../lib/api'

/**
 * The last screen, and the universal-link target (`/open?token=…`).
 *
 * Mode-aware by necessity: while the app is private there is no store page to
 * send anyone to, so the waitlist variant says "check your email" rather than
 * linking to a 404. `appStoreUrl` comes from the server precisely so this
 * doesn't depend on a build-time env var being remembered at launch.
 */

/**
 * Android's store destination. The App Store URL is no use there, and during
 * Google Play's closed test the Play listing shows "item not found" to anyone who
 * has not joined, so Android goes to the page that explains how to join. On
 * public launch, swap this for the Play listing.
 */
const ANDROID_EARLY_ACCESS_URL = 'https://bonded.studio/android/'
const isAndroid = typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent)

export function HandoffScreen({ open = false }: { open?: boolean }) {
  const { t } = useTranslation()
  const config = useFunnelConfig()
  const [params] = useSearchParams()
  const [token] = useState(() => params.get('token'))

  useEffect(() => {
    // Strip the token from the address bar so it can't be shoulder-surfed or
    // leak through a shared URL.
    if (token) window.history.replaceState(null, '', '/open')
  }, [token])

  useEffect(() => {
    void eventOnce(initialSessionId, 'handoff_viewed', { mode: config.mode }).catch(() => {})
  }, [config.mode])

  // Only ever swapped, never invented: with no App Store URL there is no store
  // link at all, and the pre-launch check below keeps working as before.
  const storeUrl = config.appStoreUrl && isAndroid ? ANDROID_EARLY_ACCESS_URL : config.appStoreUrl
  const deepLink = token
    ? `bondedapp://verify?token=${encodeURIComponent(token)}`
    : `bondedapp://funnel?session=${encodeURIComponent(initialSessionId)}`

  const openApp = () => {
    const started = Date.now()
    window.location.assign(deepLink)
    // If the scheme didn't resolve we're still visible a moment later — fall
    // back to the store, but only when we actually have one to fall back to.
    if (!storeUrl) return
    window.setTimeout(() => {
      if (document.visibilityState === 'visible' && Date.now() - started < 2500) {
        window.location.assign(storeUrl)
      }
    }, 1300)
  }

  // Pre-launch: no app to open, so the whole screen is the email confirmation.
  const preLaunch = config.mode === 'waitlist' && !storeUrl && !token

  return (
    <div className="content-column text-center">
      <div className="handoff-phone" aria-hidden>
        <span>♥</span>
      </div>
      <h1 className="value-title">{preLaunch ? t('handoff.title') : t('handoff.waitTitle')}</h1>
      <p className="body-copy">{preLaunch ? t('handoff.body') : t('handoff.email')}</p>

      {!preLaunch ? (
        <>
          <Button block size="lg" variant="sunshine" onClick={openApp}>
            {t('handoff.open')} →
          </Button>
          {storeUrl ? (
            <a className="store-link" href={storeUrl}>
              {t(isAndroid ? 'handoff.storeAndroid' : 'handoff.store')}
            </a>
          ) : null}
        </>
      ) : (
        <div className="notice">✉ {t('handoff.email')}</div>
      )}

      {open ? <p className="fine-print">{t('handoff.email')}</p> : null}
    </div>
  )
}
